// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Navigacija
import { Routes } from "../../navigation";
import { Routes as FleetTireRoutes } from "ui/modules/fleet/tires/navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { Routes as FleetWarehouseRoutes } from "ui/modules/fleet/warehouses/navigation";
// Helpers
import { transformRoute, useTranslate } from "helpers";

// API
import { deleteFleetTireSetAPI, getFleetTiresSetAPI } from "services/api/fleet/tires/api";
import { FleetTireSetLocationEntityType, FleetTireSetRow, FleetTireSetSearchFormValues } from "services/api/fleet/tires/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Komponente
import { TireSetSearchPageCmp, TireSetSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";

// Stranice / Komponente pretrage
import TireSetSearchResults from "./components/TireSetSearchResults/TireSetSearchResults";
import TireSetSearchFilters from "./components/TireSetSearchFilters/TireSetSearchFilters";

// Konstante
import {
  FLEET_TIRE_SET_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_FLEET_TIRE_SET_NEW } from "app/components/Sidebar/consts";

import TabSearchMenu from "../components/TabSearchMenu/TabSearchMenu";
import TireSetEditCard from "../TireSetEditCard/TireSetEditCard";
import { Dialog } from "ui/components/Dialog";
import TireSetDemountEditCard from "../TireSetDemountEditCard/TireSetDemountEditCard";
import TireSetMountEditCard from "../TireSetMountEditCard/TireSetMountEditCard";
import TireSetRelocateEditCard from "../TireSetRelocateEditCard/TireSetRelocateEditCard";
import TireSetWriteOffModal from "../TireSetWriteOffModal/TireSetWriteOffModal";

const TireSetSearchPage: TireSetSearchPageCmp = (
  props: TireSetSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetTireSetRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showDemount, setShowDemount] = useState(false);
  const [showMount, setShowMount] = useState(false);
  const [showRelocate, setShowRelocate] = useState(false);
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [writeOffTireSetId, setWriteOffTireSetId] = useState<string>("");
  const [tireSetId, setTireSetId] = useState<string>("");
  const [transferTireSetId, setTransferTireSetId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    width?: number | null,
    height?: number | null,
    radius?: number | null,
    vehicleManufacturerId?: string | null,
    vehicleModelId?: string | null,
    vehicleTypeId?: string | null,
    tireManufacturerId?: string | null,
    warehouseId?: string | null,
    dot?: number | null,
    profileDepth?: number | null,
    tireTypeId?: string | null,
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetTiresSetAPI(
        pagination,
        null,
        null,
        width,
        height,
        radius,
        vehicleManufacturerId,
        vehicleModelId,
        vehicleTypeId,
        tireManufacturerId,
        warehouseId,
        dot,
        profileDepth,
        tireTypeId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(FleetTireRoutes.FleetTireSetDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback((formValues: FleetTireSetSearchFormValues) => {
    fetchSearchResult(
      searchResults.pagination,
      formValues.width,
      formValues.height,
      formValues.radius,
      formValues.vehicleManufacturerId,
      formValues.vehicleModelId,
      formValues.vehicleTypeId,
      formValues.tireManufacturerId,
      formValues.warehouseId,
      formValues.dot,
      formValues.profileDepth,
      formValues.tireTypeId
    );
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setTireSetId(id);
    setOpenDialog(true);
  }

  const handleLocationClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, entityId: string, entityName: string) => {
    event.stopPropagation();
    event.preventDefault();

    let route: string;

    switch (entityName) {
      case FleetTireSetLocationEntityType.Vehicle:
        route = transformRoute(
          FleetVehicleRoutes.FleetVehicleDetailPageRouter,
          { id: entityId }
        );
        break;

      case FleetTireSetLocationEntityType.Warehouse:
        route = transformRoute(
          FleetWarehouseRoutes.FleetWarehouseDetailPageRouter,
          { id: entityId }
        );
        break;

      default:
        console.warn("Nepoznata entityName vrijednost:", entityName);
        return;
    }

    navigate(route);
  }

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    navigate(
      transformRoute(FleetTireRoutes.FleetTireSetDetailPageRouter, { id })
    );
  }, [navigate]);

  const handleDemountClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTransferTireSetId(id);
    setShowDemount(true);
  }, []);

  const handleMountClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTransferTireSetId(id);
    setShowMount(true);
  }, []);

  const handleRelocateClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setTransferTireSetId(id);
    setShowRelocate(true);
  }, []);

  const handleWriteOffClick = useCallback((event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setWriteOffTireSetId(id);
    setShowWriteOff(true);
  }, []);

  const handleWriteOffSuccess = useCallback(() => {
    setShowWriteOff(false);
    setWriteOffTireSetId("");
    fetchSearchResult(searchResults.pagination);
  }, [searchResults.pagination]);

  const handleTransferSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setTransferTireSetId("");
    setShowDemount(false);
    setShowMount(false);
    setShowRelocate(false);
  }, [searchResults.pagination]);

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!tireSetId) return;

    try {
      const deleted = await deleteFleetTireSetAPI(tireSetId, t);

      if (deleted) {
        await fetchSearchResult(searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setTireSetId("");
    } catch (err) {
      console.error("Greška prilikom brisanja seta guma:", err);
    }
  }, [tireSetId, t]);

  const handleDeleteDialogClose = useCallback(() => {
    setTireSetId("");
    setOpenDialog(false);
  }, []);


  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex justify-between">
              <div>
                <PageTitle
                  title={FLEET_TIRE_SET_SEARCH_PAGE_TITLE.text}
                  icon={FLEET_TIRE_SET_SEARCH_PAGE_TITLE.icon}
                  iconColor={FLEET_TIRE_SET_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  children={t("admin.fleet.tire.set.search.add")}
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
          <TabSearchMenu />
        </div>

        <div className="px-0 pt-6 mx-[-1px] xl:px-6">
          <TireSetSearchFilters
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.tire.set.search.noresults")} />
          ) : (
            <TireSetSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onLocationClick={handleLocationClick}
              onEditClick={handleEditClick}
              onDemountClick={handleDemountClick}
              onMountClick={handleMountClick}
              onRelocateClick={handleRelocateClick}
              onWriteOffClick={handleWriteOffClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.tire.set.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.tire.set.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.tire.set.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.tire.set.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <TireSetEditCard
        show={showAddNewModal}
        tireSetId={null}
        modalIcon="/media/images/add-icon.svg"
        modalTitle={SIDEBAR_ITEM_FLEET_TIRE_SET_NEW.text}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <TireSetDemountEditCard
        show={showDemount}
        tireSetId={transferTireSetId}
        onSuccess={handleTransferSuccess}
        onClose={() => { setShowDemount(false); setTransferTireSetId(""); }}
      />
      <TireSetMountEditCard
        show={showMount}
        tireSetId={transferTireSetId}
        onSuccess={handleTransferSuccess}
        onClose={() => { setShowMount(false); setTransferTireSetId(""); }}
      />
      <TireSetRelocateEditCard
        show={showRelocate}
        tireSetId={transferTireSetId}
        onSuccess={handleTransferSuccess}
        onClose={() => { setShowRelocate(false); setTransferTireSetId(""); }}
      />
      {showWriteOff && writeOffTireSetId && (
        <TireSetWriteOffModal
          show={showWriteOff}
          tireSetId={writeOffTireSetId}
          onSuccess={handleWriteOffSuccess}
          onHide={() => { setShowWriteOff(false); setWriteOffTireSetId(""); }}
        />
      )}
    </>
  );
};

TireSetSearchPage.route = Routes.FleetTireSetSearchPageRouter;

export default TireSetSearchPage;
